"use client";

import { ReactNode, useState, useCallback } from "react";
import { clsx } from "clsx";
import { GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { Checkbox } from "../Checkbox";
import { Spinner } from "../Spinner";

// ============================================
// Type Definitions
// ============================================

type SortDirection = "asc" | "desc" | null;

interface Column<T> {
  id: string;
  /** Column header label */
  label: string;
  /** Accessor key or function to get cell value */
  accessor: keyof T | ((row: T) => ReactNode);
  /** Column width (CSS value) */
  width?: string;
  /** Minimum width (CSS value) */
  minWidth?: string;
  /** Enable sorting for this column */
  sortable?: boolean;
  /** Custom header render */
  renderHeader?: (column: Column<T>) => ReactNode;
  /** Custom cell render */
  renderCell?: (value: unknown, row: T, rowIndex: number) => ReactNode;
  /** Text alignment */
  align?: "left" | "center" | "right";
  /** Whether this column is sticky */
  sticky?: boolean;
}

interface DataTableProps<T> {
  /** Table data rows */
  data: T[];
  /** Column definitions */
  columns: Column<T>[];
  /** Unique key accessor for each row */
  rowKey: keyof T | ((row: T) => string);
  /** Row hover callback */
  onRowHover?: (row: T | null, index: number | null) => void;
  /** Row click callback */
  onRowClick?: (row: T, index: number) => void;
  /** Enable row selection */
  selectable?: boolean;
  /** Selected row keys */
  selectedKeys?: Set<string>;
  /** Selection change callback */
  onSelectionChange?: (keys: Set<string>) => void;
  /** Show row drag handles */
  draggable?: boolean;
  /** External sort state */
  sortColumn?: string;
  sortDirection?: SortDirection;
  onSort?: (column: string, direction: SortDirection) => void;
  /** Empty state content */
  emptyState?: ReactNode;
  /** Footer content (e.g., add row button) */
  footer?: ReactNode;
  /** Additional header actions (e.g., add column button) */
  headerActions?: ReactNode;
  /** Loading state */
  isLoading?: boolean;
  /** Compact mode */
  compact?: boolean;
  /** Custom class names */
  className?: string;
  tableClassName?: string;
  headerClassName?: string;
  rowClassName?: string | ((row: T, index: number) => string);
}

// ============================================
// Sub-components
// ============================================

function TableHeaderCell<T>({
  column,
  sortColumn,
  sortDirection,
  onSort,
}: {
  column: Column<T>;
  sortColumn?: string;
  sortDirection?: SortDirection;
  onSort?: (column: string, direction: SortDirection) => void;
}) {
  const isSorted = sortColumn === column.id;
  const canSort = column.sortable && onSort;

  const handleSort = () => {
    if (!canSort) return;

    let newDirection: SortDirection = "asc";
    if (isSorted) {
      if (sortDirection === "asc") newDirection = "desc";
      else if (sortDirection === "desc") newDirection = null;
    }
    onSort(column.id, newDirection);
  };

  const alignClass =
    column.align === "center"
      ? "justify-center text-center"
      : column.align === "right"
        ? "justify-end text-right"
        : "justify-start text-left";

  return (
    <th
      className={clsx(
        "px-4 py-3 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider",
        "border-b border-[var(--color-border)]",
        "bg-[var(--color-surface-hover)]",
        canSort && "cursor-pointer hover:bg-[var(--color-border)] transition-colors",
        column.sticky && "sticky left-0 z-10"
      )}
      style={{
        width: column.width,
        minWidth: column.minWidth || "100px",
      }}
      onClick={handleSort}
    >
      <div className={clsx("flex items-center gap-1.5", alignClass)}>
        {column.renderHeader ? column.renderHeader(column) : column.label}
        {canSort && (
          <span className="flex flex-col -my-1">
            <ChevronUp
              className={clsx(
                "w-3 h-3 -mb-1",
                isSorted && sortDirection === "asc"
                  ? "text-[var(--color-text-primary)]"
                  : "text-[var(--color-border)]"
              )}
            />
            <ChevronDown
              className={clsx(
                "w-3 h-3",
                isSorted && sortDirection === "desc"
                  ? "text-[var(--color-text-primary)]"
                  : "text-[var(--color-border)]"
              )}
            />
          </span>
        )}
      </div>
    </th>
  );
}

function TableRow<T>({
  row,
  rowIndex,
  columns,
  isHovered,
  isSelected,
  onClick,
  onHover,
  draggable,
  selectable,
  onSelect,
  className,
}: {
  row: T;
  rowIndex: number;
  columns: Column<T>[];
  rowKey: string;
  isHovered: boolean;
  isSelected: boolean;
  onClick?: () => void;
  onHover: (hovered: boolean) => void;
  draggable?: boolean;
  selectable?: boolean;
  onSelect?: () => void;
  className?: string;
}) {
  return (
    <tr
      className={clsx(
        "border-b border-[var(--color-border-light)]",
        "transition-all duration-150",
        isHovered && "bg-[var(--color-surface-hover)]",
        isSelected && "bg-blue-500/10",
        onClick && "cursor-pointer",
        className
      )}
      style={{ height: "44px" }}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onClick={onClick}
    >
      {/* Drag handle / selection column */}
      {(draggable || selectable) && (
        <td className="w-10 px-2">
          <div
            className={clsx(
              "flex items-center gap-1 transition-opacity",
              isHovered ? "opacity-100" : "opacity-0"
            )}
          >
            {draggable && (
              <GripVertical className="w-4 h-4 text-[var(--color-text-muted)] cursor-grab" />
            )}
            {selectable && (
              <Checkbox
                size="sm"
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onSelect?.();
                }}
              />
            )}
          </div>
        </td>
      )}

      {/* Data cells */}
      {columns.map((column) => {
        const value =
          typeof column.accessor === "function"
            ? column.accessor(row)
            : row[column.accessor];

        const alignClass =
          column.align === "center"
            ? "text-center"
            : column.align === "right"
              ? "text-right"
              : "text-left";

        return (
          <td
            key={column.id}
            className={clsx(
              "px-4 py-2.5 text-sm text-[var(--color-text-secondary)]",
              alignClass,
              column.sticky && "sticky left-0 bg-inherit"
            )}
            style={{
              minWidth: column.minWidth || "100px",
              width: column.width,
            }}
          >
            {column.renderCell
              ? column.renderCell(value, row, rowIndex)
              : (value as ReactNode)}
          </td>
        );
      })}
    </tr>
  );
}

// ============================================
// Main Component
// ============================================

export function DataTable<T extends Record<string, unknown>>({
  data,
  columns,
  rowKey,
  onRowHover,
  onRowClick,
  selectable = false,
  selectedKeys = new Set(),
  onSelectionChange,
  draggable = false,
  sortColumn,
  sortDirection,
  onSort,
  emptyState,
  footer,
  headerActions,
  isLoading = false,
  compact = false,
  className,
  tableClassName,
  headerClassName,
  rowClassName,
}: DataTableProps<T>) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Get row key
  const getRowKey = useCallback(
    (row: T): string => {
      if (typeof rowKey === "function") {
        return rowKey(row);
      }
      return String(row[rowKey]);
    },
    [rowKey]
  );

  // Handle row hover
  const handleRowHover = useCallback(
    (index: number, hovered: boolean) => {
      const newIndex = hovered ? index : null;
      setHoveredIndex(newIndex);
      onRowHover?.(hovered ? data[index] : null, newIndex);
    },
    [data, onRowHover]
  );

  // Handle row selection
  const handleRowSelect = useCallback(
    (key: string) => {
      const newKeys = new Set(selectedKeys);
      if (newKeys.has(key)) {
        newKeys.delete(key);
      } else {
        newKeys.add(key);
      }
      onSelectionChange?.(newKeys);
    },
    [selectedKeys, onSelectionChange]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" label="Loading..." />
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "overflow-x-auto scrollbar-thin bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]",
        className
      )}
    >
      <table
        className={clsx(
          "w-full border-collapse",
          compact ? "text-sm" : "text-base",
          tableClassName
        )}
      >
        {/* Header */}
        <thead className={headerClassName}>
          <tr>
            {/* Drag handle / selection header */}
            {(draggable || selectable) && (
              <th className="w-10 px-2 border-b border-[var(--color-border)] bg-[var(--color-surface-hover)]">
                {selectable && (
                  <Checkbox
                    size="sm"
                    checked={selectedKeys.size === data.length && data.length > 0}
                    indeterminate={selectedKeys.size > 0 && selectedKeys.size < data.length}
                    onChange={() => {
                      if (selectedKeys.size === data.length) {
                        onSelectionChange?.(new Set());
                      } else {
                        onSelectionChange?.(new Set(data.map((row) => getRowKey(row))));
                      }
                    }}
                  />
                )}
              </th>
            )}

            {columns.map((column) => (
              <TableHeaderCell
                key={column.id}
                column={column}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={onSort}
              />
            ))}

            {/* Header actions column */}
            {headerActions && (
              <th className="w-auto min-w-[80px] border-b border-[var(--color-border)] bg-[var(--color-surface-hover)]">
                {headerActions}
              </th>
            )}
          </tr>
        </thead>

        {/* Body */}
        <tbody>
          {data.map((row, index) => {
            const key = getRowKey(row);
            const isHovered = hoveredIndex === index;
            const isSelected = selectedKeys.has(key);
            const customRowClass =
              typeof rowClassName === "function"
                ? rowClassName(row, index)
                : rowClassName;

            return (
              <TableRow
                key={key}
                row={row}
                rowIndex={index}
                columns={columns}
                rowKey={key}
                isHovered={isHovered}
                isSelected={isSelected}
                onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                onHover={(hovered) => handleRowHover(index, hovered)}
                draggable={draggable}
                selectable={selectable}
                onSelect={() => handleRowSelect(key)}
                className={customRowClass}
              />
            );
          })}
        </tbody>

        {/* Footer */}
        {footer && (
          <tfoot>
            <tr>
              <td
                colSpan={
                  columns.length + (draggable || selectable ? 1 : 0) + (headerActions ? 1 : 0)
                }
              >
                {footer}
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      {/* Empty state */}
      {data.length === 0 &&
        (emptyState || (
          <div className="flex flex-col items-center justify-center py-16 text-[var(--color-text-muted)]">
            <div className="w-16 h-16 mb-4 rounded-full bg-[var(--color-surface-hover)] flex items-center justify-center">
              <svg
                className="w-8 h-8 text-[var(--color-text-muted)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-base font-medium mb-1">No data</p>
            <p className="text-sm">Add items to get started</p>
          </div>
        ))}
    </div>
  );
}

export type { DataTableProps, Column, SortDirection };
